import { type api, useApiQuery } from '@vrplatform/data';
import { formatOwnerName } from '@vrplatform/ui-common';
import { day, sum } from '@vrplatform/utils';
import { useOwnerStatementSummariesDetailContext } from '../detail/_context';

export const STORYBOOK_OWNER_STATEMENT_SUMMARIES_TABLE_QUERY_KEY =
  'owner-statement-summaries-table';

export const getOwnerStatementSummariesQueryFilterParams = (
  monthRange: `${string}...${string}`
): api['/reports/owner-statement-summaries-per-layout']['get']['parameters']['query'] => {
  if (monthRange.length === 4) {
    const year = day().set('year', Number(monthRange));

    const endAt = year.endOf('year').add(1, 'day').yyyymmdd();
    const startAt = year.startOf('year').yyyymmdd();
    return {
      endAt,
      startAt,
    };
  }

  const [startAt, endAt] = monthRange.split('...');
  return {
    endAt: day(endAt).add(1, 'day').yyyymmdd(),
    startAt,
  };
};

export type OwnerStatementSummariesTableData = NonNullable<
  ReturnType<typeof useOwnerStatementSummariesTableQuery>['data']
>;

export type OwnerStatementSummariesTableRow =
  OwnerStatementSummariesTableData['rows'][0];

export const useOwnerStatementSummariesTableQuery = () => {
  const { monthRange } = useOwnerStatementSummariesDetailContext();
  return useApiQuery(
    async (api, signal, args) => {
      return await api
        .GET('/reports/owner-statement-summaries-per-layout', {
          params: {
            query: getOwnerStatementSummariesQueryFilterParams(args.monthRange),
          },
          signal,
        })
        .then((res) => {
          if (res.error) throw res.error;
          const rows = res.data.data.map((item) => {
            return {
              id: item.contact.contactId,
              ...item,
              contact: {
                ...item.contact,
                formattedName:
                  formatOwnerName(item.contact, {
                    lastNameFirst: true,
                  }) || '-',
              },
            };
          });

          const totals: Record<
            Exclude<keyof (typeof rows)[0]['financials'], 'transfers'>,
            number
          > = {
            balanceStart: sum(rows, (x) => x.financials.balanceStart),
            balanceEnd: sum(rows, (x) => x.financials.balanceEnd),
            netRevenue: sum(rows, (x) => x.financials.netRevenue),
            expenses: sum(rows, (x) => x.financials.expenses),
            payouts: sum(rows, (x) => x.financials.payouts),
            netIncome: sum(rows, (x) => x.financials.netIncome),
            reserve: sum(rows, (x) => x.financials.reserve),
            currentBalance: sum(rows, (x) => x.financials.currentBalance),
          };

          return {
            rows,
            totals,
          };
        });
    },
    {
      variables: {
        monthRange,
      },
      storybookQueryKey: STORYBOOK_OWNER_STATEMENT_SUMMARIES_TABLE_QUERY_KEY,
      queryGroup: 'owner-statements',
      uniqueQueryKey: 'owner-statement-summaries-report',
      queryKeys: ['journalEntries', 'reports'],
      keepPreviousData: true,
    }
  );
};
